import { createContext, useEffect, useReducer, ReactElement } from "react";
import { toast } from "react-toastify";
import jwtDecode from "jwt-decode";

// reducer - state management
import { LOGIN, LOGOUT } from "store/reducers/actions";
import authReducer from "store/reducers/auth";

// project-imports
// import Loader from "components/Loader";
import { AuthProps, UserContextType } from "types/auth";
import { setAuthToken, setAuthUser } from "libs/HttpClient";
import { KeyedObject } from "types/root";
import { decryptValues } from "helpers/commonHelpers";
import CircularLoader from "components/CircularLoader";

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);

  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

// ==============================|| Auth CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<UserContextType | null>(null);

export const JWTProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        let data = null;
        const serviceToken = localStorage.getItem("serviceToken");
        const encryptedUserData = localStorage.getItem("userData");
        if (encryptedUserData) {
          const decryptedUserData = decryptValues(
            JSON.parse(encryptedUserData)
          ); // Decrypt the encrypted user data
          data = JSON.parse(decryptedUserData); // Parse the decrypted data to get the original user object
        }
        if (serviceToken && verifyToken(serviceToken)) {
          setAuthToken(serviceToken);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: data,
            },
          });
        } else {
          dispatch({
            type: LOGOUT,
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT,
        });
      }
    };

    init();
  }, []);

  // useEffect(() => {
  //   let logoutTimer: NodeJS.Timeout;

  //   const resetTimer = () => {
  //     if (logoutTimer) {
  //       clearTimeout(logoutTimer);
  //     }
  //     logoutTimer = setTimeout(() => {
  //       dispatch({ type: LOGOUT });
  //     }, 10 * 60 * 1000); // 1 minutes
  //   };

  //   const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];

  //   events.forEach((event) => {
  //     window.addEventListener(event, resetTimer);
  //   });

  //   resetTimer(); // Initialize the timer when the component mounts

  //   return () => {
  //     events.forEach((event) => {
  //       window.removeEventListener(event, resetTimer);
  //     });
  //     if (logoutTimer) {
  //       clearTimeout(logoutTimer);
  //     }
  //   };
  // }, [state.isLoggedIn]);

  const login = async (user: any, token: string): Promise<void> => {
    try {
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: user,
          token: token,
        },
      });
      setAuthToken(token);
      setAuthUser(user);
    } catch (error) {
      // Handle unexpected errors here
      toast.error("An unexpected error occurred during login"); // Adjust this message accordingly
    }
  };

  const logout = () => {
    dispatch({ type: LOGOUT });
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <CircularLoader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
